/**
 * KAN-722: карточка разбора zip от продавца (archive/inspect/) до импорта:
 * формат, номер, наличие 2FA, подобранная метка и предупреждения сервера.
 */
import { Loader2 } from 'lucide-react';
import { ARCHIVE_FORMAT_LABELS, describeInspectDuplicate } from '@/lib/telegramConnect';
import type { ArchiveInspectResult } from '@/types/telegramAccount';

export const PHONE_NOT_FOUND = 'не найден в архиве';
export const PHONE_FROM_NAME = '(из имени файла)';

/** Номер для сводки: с пометкой, если сервер взял его из имени архива или файла внутри. */
function phoneText(result: ArchiveInspectResult): string {
  if (!result.phone) return PHONE_NOT_FOUND;
  const fromName = result.phone_source === 'archive_name' || result.phone_source === 'file_name';
  return fromName ? `${result.phone} ${PHONE_FROM_NAME}` : result.phone;
}

interface Props {
  result: ArchiveInspectResult | null;
  pending: boolean;
  error: string | null;
}

export function ArchiveInspectCard({ result, pending, error }: Props): JSX.Element | null {
  if (pending) {
    return (
      <p className="flex items-center text-xs text-gray-500" data-testid="archive-inspect-pending">
        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
        Разбираем архив...
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-xs text-amber-700" data-testid="archive-inspect-error">
        Не удалось разобрать архив: {error}. Подключить можно и без разбора.
      </p>
    );
  }
  if (!result) return null;
  const format = ARCHIVE_FORMAT_LABELS[result.format] ?? result.format;
  // KAN-739: номер уже в кабинете; метку не предлагаем, форма блокирует кнопку
  const duplicateText = describeInspectDuplicate(result.duplicate);
  return (
    <div className="rounded-md border bg-gray-50 px-3 py-2 text-sm" data-testid="archive-inspect-card">
      <p data-testid="archive-inspect-summary">
        Распознано: {format}, номер {phoneText(result)}, 2FA{' '}
        {result.two_fa_present ? 'есть' : 'нет'}
      </p>
      {duplicateText && (
        <p
          role="alert"
          className="mt-1 text-sm font-medium text-red-600"
          data-testid="archive-inspect-duplicate"
        >
          {duplicateText}
        </p>
      )}
      {!duplicateText && result.suggested_label && (
        <p className="text-xs text-gray-600" data-testid="archive-inspect-label">
          Метка: {result.suggested_label}
        </p>
      )}
      {result.warnings.length > 0 && (
        <ul className="mt-1 list-disc pl-4 text-xs text-amber-700" data-testid="archive-inspect-warnings">
          {result.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ArchiveInspectCard;
